import { onlineManager, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { AdminNetworkError, AdminRequestAbortedError } from '~/api/errors';
import { type RefundRow, useRefundNote } from '~/data/queries';
import { financeApi } from '~/hooks/use-finance';
import { currencySymbol, fmtDateTime } from '~/lib/format';
import { Drawer } from '~/ui/Dialog';
import { FormRow } from '~/ui/FormRow';
import { Row } from '~/ui/Pair';
import { Stat } from '~/ui/StatCard';
import { toast } from '~/ui/Toast';

// =========================================================
// Refund Detail (drawer) — 原 dialogs2.jsx 172–250
// =========================================================
export function RefundDetail({ refund, onClose }: { refund: RefundRow; onClose: () => void }) {
  const r = refund;
  // 运营备注（ADM-FIN-S003）：抽屉打开即取，本地受控编辑 → 保存写 admin 独有表 refund_admin_notes。
  // 退款「原因」仍为 vendor 侧只读字段（契约 A1 §4 无 reason），维持 disabled（SCHEMA_BLOCKED）。
  const { data: noteData } = useRefundNote(r.id, true, r.environment ?? 'Production');
  const queryClient = useQueryClient();
  // draft=null 表示「未编辑、跟随远端」；一旦编辑用本地草稿。避免 GET /note 异步到达时
  // useEffect 把用户已输入内容清掉的竞态（远端晚到不覆盖本地编辑）。保存成功后回 null 重新跟随远端。
  const serverNote = noteData?.note ?? '';
  const [draft, setDraft] = useState<string | null>(null);
  const note = draft ?? serverNote;
  const noteDirty = draft !== null && draft !== serverNote;
  const saveNote = useMutation({
    // 备注不延迟排队或自动重试，防关闭抽屉后重连覆盖后来录入的备注。
    networkMode: 'always',
    retry: false,
    mutationFn: (next: string) => {
      if (!onlineManager.isOnline())
        throw new Error('网络不可用，本次备注未发送；恢复后请重新确认并保存。');
      return financeApi.updateRefundNote(r.id, next, r.environment ?? 'Production');
    },
    onSuccess: () => {
      toast('运营备注已保存', { tone: 'pos', icon: 'IconCheck' });
      setDraft(null);
      queryClient.invalidateQueries({
        queryKey: ['refund-note', r.id, r.environment ?? 'Production'],
      });
    },
    onError: (err) => {
      // 请求已发送后丢失响应不能证明服务端未写；再次提交前需重读确认。
      const resultUnknown =
        err instanceof AdminNetworkError ||
        (err instanceof AdminRequestAbortedError && err.serverStateUnknown);
      toast(
        resultUnknown
          ? '备注保存结果未知，请重新打开退款单核对后再保存。'
          : err instanceof Error
            ? err.message
            : '保存失败',
        { tone: 'neg', icon: 'IconAlert' },
      );
    },
  });
  return (
    <Drawer
      title={`退款单 ${r.id}`}
      subtitle={`订单 ${r.orderId} · ${fmtDateTime(r.createdAt)}`}
      icon="IconWallet"
      onClose={onClose}
      width={680}
    >
      <div
        style={{
          padding: 18,
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 14,
          borderBottom: '1px solid var(--border)',
        }}
      >
        <Stat label="金额" v={`${currencySymbol(r.currency)}${r.amount}`} />
        {/* F2：移除「渠道」Stat——退款 DTO（契约 A1 §4）无 channel 字段，与 RefundRecords 去渠道列对齐，不渲染恒空单元格 */}
        <Stat
          label="类型"
          v={
            <span style={{ color: r.type === '被动退款' ? 'var(--neg)' : 'var(--pos)' }}>
              {r.type}
            </span>
          }
        />
        <Stat
          label="状态"
          v={
            <span
              className={`chip ${
                r.status === '已退款'
                  ? 'chip-pos'
                  : r.status === '已驳回'
                    ? 'chip-neg'
                    : 'chip-warn'
              }`}
            >
              {r.status}
            </span>
          }
        />
      </div>

      <div style={{ padding: 18, borderBottom: '1px solid var(--border)' }}>
        {/* 退款只读镜像（契约 A1 §4）：admin 退款记录 = vendor webhook 只读镜像，无中间态、无审批写路径，
            恒 COMPLETED/PASSIVE。原型「批准退款 / 驳回」审批按钮是 contract-mismatch（P-29）→ 已移除（见 docs/plans §五）。 */}
        <div className="hstack" style={{ marginBottom: 10 }}>
          <span style={{ fontWeight: 500, fontSize: 13 }}>处理时间线</span>
          <div style={{ flex: 1 }} />
          <span
            className="chip mono"
            style={{ fontSize: 10 }}
            title="退款记录为 vendor 渠道 webhook 只读镜像（契约 A1 §4），无 admin 审批写动作"
          >
            只读镜像
          </span>
        </div>
        <div className="vstack" style={{ gap: 0 }}>
          <div
            className="hstack"
            style={{
              gap: 14,
              padding: '10px 0',
              alignItems: 'flex-start',
            }}
          >
            <div
              style={{
                width: 6,
                height: 6,
                borderRadius: 999,
                marginTop: 8,
                flexShrink: 0,
                background: r.status === '已退款' ? 'var(--pos)' : 'var(--accent)',
              }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12.5 }}>
                <span style={{ fontWeight: 500 }}>system</span> · 退款发生
              </div>
              {r.status === '已退款' && (
                <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 3 }}>
                  vendor 渠道完成退款
                </div>
              )}
              <div
                className="mono"
                style={{ fontSize: 10.5, color: 'var(--fg-faint)', marginTop: 2 }}
              >
                {fmtDateTime(r.createdAt)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          padding: 18,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 18,
          borderBottom: '1px solid var(--border)',
        }}
      >
        <div>
          <div className="label" style={{ marginBottom: 8 }}>
            订单信息
          </div>
          {/* 退款只读镜像（契约 A1 §4）：仅展示退款 DTO 真有的字段——orderId / appName / amount(原币)。
              「支付渠道」「原始购买明细」DTO 不提供（去渠道列、无 line-item 端点）→ 不渲染恒空/杜撰单元格（NOT-in-scope）。 */}
          <Row k="订单号" v={r.orderId} mono />
          <Row k="App" v={r.appName} />
          <Row k="金额" v={`${currencySymbol(r.currency)}${r.amount}`} last />
        </div>
        <div>
          <div className="label" style={{ marginBottom: 8 }}>
            用户
          </div>
          {/* 退款 DTO 仅给 userId；用户解析（昵称 / 国家 / 累计充值 / 历史退款 / 风控状态）暂无关联端点
              （NOT-in-scope，docs/plans 2026-06-12 §五）→ 显式空态 + 仅展示真实 userId，不渲染杜撰身份与统计。 */}
          <Row k="用户 ID" v={r.userId} mono last />
          <div
            className="mono"
            style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 8 }}
            title="用户解析 / 累计充值 / 历史退款 / 风控状态契约暂未提供关联端点"
          >
            用户详情、累计充值、历史退款、风控状态：契约暂未提供
          </div>
        </div>
      </div>

      <div style={{ padding: 18 }}>
        <FormRow
          label="退款原因"
          hint="用户填写或运营备注"
          labelWidth={140}
          gap={14}
          pad="12px 0"
          labelPadTop={6}
        >
          {/* 退款 DTO（契约 A1 §4）不提供 reason 字段，无原始购买明细端点 → 不预填杜撰申诉文案；
              留空供运营录入（NOT-in-scope，docs/plans 2026-06-12 §五）。 */}
          {/* 退款为只读 webhook 镜像（契约 A1 §4 无 reason 写端点）→ disabled 占位，不做"可输入却永不持久"的假象；
              如需运营补录退款原因/备注，须先 dev-docs PR 加 admin 退款备注表 + PATCH 端点（计划 §五）。 */}
          <textarea
            className="input"
            rows={2}
            placeholder="退款为只读镜像，暂无退款原因录入端点（计划 §五）"
            style={{ height: 'auto' }}
            disabled
            title="退款为只读 webhook 镜像，暂无录入端点（计划 §五）"
          />
        </FormRow>
        {/* 运营备注接真（ADM-FIN-S003）：写 admin 独有表 refund_admin_notes（不碰 C 端）。可编辑 + 保存。 */}
        <FormRow label="运营备注" labelWidth={140} gap={14} pad="12px 0" labelPadTop={6}>
          <div className="vstack" style={{ gap: 8, alignItems: 'flex-start', width: '100%' }}>
            <textarea
              className="input"
              rows={2}
              placeholder="给该退款补录内部备注（仅运营可见）"
              style={{ height: 'auto', width: '100%' }}
              value={note}
              maxLength={2000}
              onChange={(e) => setDraft(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!noteDirty || saveNote.isPending}
              onClick={() => {
                if (!onlineManager.isOnline()) {
                  toast('网络不可用，本次备注未发送；恢复后请重新确认并保存。', {
                    tone: 'neg',
                    icon: 'IconAlert',
                  });
                  return;
                }
                saveNote.mutate(note);
              }}
            >
              {saveNote.isPending ? '保存中…' : '保存备注'}
            </button>
          </div>
        </FormRow>
      </div>
    </Drawer>
  );
}
